import React, { useContext, useMemo, useState } from "react";
import { useLang, useTextDomainContext } from "@/app/providers";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { RolesDropdown } from "@/components";
import { useAddUserToPartner, useAddUserToTenant } from "@/hooks";
import { getPartnerKey } from "@/utils";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import MailOutlineIcon from "@mui/icons-material/MailOutlineRounded";

interface LinkUserRow {
  tenantId?: string;
  partnerId?: string;
  userFilter?: "tenant" | "partner" | string;
  [key: string]: any;
}

interface LinkUserProps {
  open?: boolean;
  handleClose: () => void;
  row: LinkUserRow;
}

export const LinkUser = (props: LinkUserProps): React.ReactElement => {
  //#region Loading data
  // const { enqueueSnackbar } = useSnackbar();
  const { row, open } = props;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const tenantId = props?.row?.tenantId || "";
  const partnerId = props?.row?.partnerId || "";
  const locale = useLang();
  const { addUserToPartner } = useAddUserToPartner();
  const { addUserToTenant } = useAddUserToTenant();
  //#endregion Loading data

  //#region useMemo
  const rolesType = useMemo(() => {
    let returnValue = "";
    if (row.userFilter === "tenant") {
      returnValue = "company";
    }

    if (row.userFilter === "partner") {
      returnValue = "partner";
    }
    return returnValue;
  }, [row.userFilter]);

  const defaultInputRole = useMemo(() => {
    let returnValue = {};

    if (rolesType === "company") {
      returnValue = {
        label: "Company User",
        value: "company-user",
      };
    }

    if (rolesType === "partner") {
      returnValue = {
        label: "Partner User",
        value: "partner-user",
      };
    }

    return returnValue;
  }, [rolesType]);

  //#endregion useMemo

  //#region useState
  // const [tenantId, setTenantId] = useState("");
  // const [companyId, setCompanyId] = useState("");
  const [inputEmail, setInputEmail] = useState("");
  // const [inputRole, setInputRole] = useState({
  //   label: "Company User",
  //   value: "company-user",
  // });
  const [inputRole, setInputRole] = useState<any>(defaultInputRole);
  // const [rolesType, setRolesType] = useState("company");
  const [notifyUser, setNotifyUser] = useState(true);
  //#endregion useState

  //#region useEffect
  // useEffect(() => {
  //   if (tenantId) {
  //     // setRolesType("company");
  //     if (!inputRole.value) {
  //       setInputRole({
  //         label: "Company User",
  //         value: "company-user",
  //       });
  //     }
  //   }
  //
  //   if (partnerId) {
  //     // setRolesType("partner");
  //     if (!inputRole.value) {
  //       setInputRole({
  //         label: "Partner User",
  //         value: "partner-user",
  //       });
  //     }
  //   }
  // }, [inputRole, partnerId, tenantId]);

  //#endregion useEffect

  //#region Handlers
  const handleRoleChange = (event: any) => {
    setInputRole(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotifyUser(event.target.checked);
  };

  // const { addUserByAdmin, loading: adding } = useAddUserByAdmin(
  //   () => {
  //     handleClose();
  //     enqueueSnackbar(gettext("The new user has been added."), {
  //       variant: "success",
  //     });
  //   },
  //   (error) => {
  //     enqueueSnackbar(error.message, {
  //       variant: "error",
  //     });
  //   },
  // );

  // const { updateUser, loading: updating } = useUpdateUser(
  //   () => {
  //     handleClose();
  //     enqueueSnackbar(gettext("The user has been updated"), {
  //       variant: "success",
  //     });
  //   },
  //   (error) => {
  //     enqueueSnackbar(error.message, {
  //       variant: "error",
  //     });
  //   },
  // );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const variables = {
      tenantId,
      partnerId,
      email: inputEmail,
      roleKey: inputRole?.key,
      languageCode: locale,
      partnerKey: getPartnerKey(),
      notifyUser,
    };

    if (partnerId) {
      addUserToPartner({
        variables,
      }).then();
    }

    if (tenantId) {
      addUserToTenant({
        variables,
      }).then();
    }
  };

  const handleClose = () => {
    props.handleClose();
  };

  //endregion Handlers

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            minWidth: 300,
            maxWidth: 500,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {gettext("Link user")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid
                container
                spacing={4}
                sx={{
                  height: "100%",
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="email"
                    id="email"
                    label={gettext("Email")}
                    placeholder={gettext("Enter email")}
                    size="small"
                    required
                    fullWidth
                    autoComplete="off"
                    value={inputEmail}
                    onChange={(e) => {
                      setInputEmail(e.target.value);
                    }}
                    // InputProps={{
                    //   readOnly: true,
                    // }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <RolesDropdown
                    defaultValue={inputRole?.value}
                    componentLabel={gettext("Roles")}
                    type={rolesType}
                    onChange={handleRoleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        icon={<MailOutlineIcon />}
                        checkedIcon={<MarkunreadIcon />}
                        checked={notifyUser}
                        onChange={handleChange}
                      />
                    }
                    label={gettext("Send the user a notification via email.")}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid
                container
                spacing={4}
                sx={{
                  width: "100%",
                }}
              >
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={false}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Link")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};
